import * as React from 'react';
import propz from 'propz';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { AppUser } from 'Src/views/App/App';
import { Tournament } from 'Src/models/tournament';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/report';
import { uploadReport } from 'Src/helpers/service/admin/report';
import { downloadTournamentMeetPack } from 'Src/helpers/service/adminSU/tournamentsTable';
import { isIndividualTournament, isTeamTournament } from 'Src/helpers/tournament/tournament';
import { TournamentSchoolsReportForm } from './TournamentSchoolsReportForm/TournamentSchoolsReportForm';
import { TournamentTeamMembersReportForm } from './TournamentTeamMembersReportForm/TournamentTeamMembersReportForm';
import { TournamentParticipationConfirmationsReportForm } from './TournamentParticipationConfirmationsReportForm/TournamentParticipationConfirmationsReportForm';
import { TournamentStartListReportForm } from './TournamentStartListReportForm/TournamentStartListReportForm';
import { TournamentPlayersReportForm } from './TournamentPlayersReportForm/TournamentPlayersReportForm';
import { HighLevelTournamentSchoolAdminsReportForm } from './HighLevelTournamentSchoolAdminsReportForm/HighLevelTournamentSchoolAdminsReportForm';
import { TournamentScoreCardsReportForm } from './TournamentScoreCardsReportForm/TournamentScoreCardsReportForm';
import { TournamentResultsIndividualReportForm } from './TournamentResultsIndividualReportForm/TournamentResultsIndividualReportForm';
import '../Tournaments.css';

type ReportKey =
  | 'schools'
  | 'members'
  | 'confirmations'
  | 'players'
  | 'contacts'
  | 'start'
  | 'results'
  | 'scores'
  | 'meet';

interface Props {
  user: AppUser;
  tournament: Tournament;
  isOpen: boolean;
  onClose: () => void;
}
interface State {
  activeReport?: ReportKey;
  isLoading: boolean;
  error: string;
}

export class TournamentReportsLauncher extends React.Component<Props, State> {
  state: State = { isLoading: false, error: '' };

  selectReport = (activeReport: ReportKey) => this.setState({ activeReport, error: '' });
  closeParameters = () => this.setState({ activeReport: undefined, error: '' });

  downloadMeetPack = () => {
    const { user, tournament } = this.props;
    this.setState({ isLoading: true, error: '' });
    downloadTournamentMeetPack(user, tournament.id)
      .then(({ blob, filename }) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'meet-pack.zip';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        this.setState({ isLoading: false });
      })
      .catch(error =>
        this.setState({
          isLoading: false,
          error: propz.get(
            error,
            ['response', 'data', 'details', 'text'],
            error.message || "Couldn't download the meet pack."
          )
        })
      );
  };

  onReportClick = (report: { key: ReportKey }) => {
    if (report.key === 'meet') this.downloadMeetPack();
    else this.selectReport(report.key);
  };

  submit = (data: any, reportType: string) => {
    const { user, tournament } = this.props;
    const params: any = { unionId: user.activeSchoolId, tournamentId: tournament.id };
    switch (reportType) {
      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.TOURNAMENT_SCHOOLS:
      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.HIGH_LEVEL_TOURNAMENT_SCHOOL_ADMINS:
        params.showAllAdmins = data.isShowAllAdmins;
        break;
      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.TOURNAMENT_TEAM_MEMBERS:
        params.showBlank = data.isShowBlank;
        params.highlightNoAge = data.isHighlightNoAge;
        break;
      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.TOURNAMENT_PLAYERS:
        params.showBlank = data.isShowBlank;
        break;
      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.TOURNAMENT_PARTICIPATION_CONFIRMATIONS:
        params.showAccepted = data.isShowAccepted;
        params.showRejected = data.isShowRejected;
        params.showUnresponded = data.isShowUnresponded;
        params.showNotSent = data.isShowNotSent;
        params.printResponseMetadata = true;
        break;
      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.TOURNAMENT_START_LIST:
        params.showBlank = data.isShowBlank;
        params.highlightNoAge = data.isHighlightNoAge;
        params.printScoreCards = data.isPrintScoreCards;
        break;
      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.TOURNAMENT_SCORE_CARDS:
        params.showSchedule = data.isShowSchedule;
        break;
      case REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.TOURNAMENT_RESULTS_INDIVIDUAL:
        params.showBlank = data.isShowBlank;
        params.printFullNames = data.isPrintFullNames;
        break;
    }
    this.setState({ isLoading: true, error: '' });
    uploadReport(user, reportType, { reportParamsCSV: params })
      .then(result => {
        if (!result || typeof result.reportId === 'undefined') throw new Error('The report could not be generated.');
        window.open(`${window.apiBase}/public/report/${result.reportId}/reportType/${result.reportType}`);
        this.setState({ isLoading: false, activeReport: undefined });
        this.props.onClose();
      })
      .catch(error =>
        this.setState({
          isLoading: false,
          error: propz.get(
            error,
            ['response', 'data', 'details', 'text'],
            error.message || 'The report could not be generated.'
          )
        })
      );
  };

  getReports() {
    const reports: Array<{ key: ReportKey; name: string; description: string }> = [
      { key: 'schools', name: 'Tournament schools', description: 'Schools taking part in this tournament.' },
      { key: 'members', name: 'Tournament team members', description: 'Team members entered in this tournament.' },
      {
        key: 'confirmations',
        name: 'Tournament participation confirmations',
        description: 'Participation confirmation status for this tournament.'
      },
      { key: 'players', name: 'Tournament players', description: 'Player list for this tournament.' },
      {
        key: 'contacts',
        name: 'Tournament schools contact details',
        description: 'Contact details for participating schools.'
      }
    ];
    if (isIndividualTournament(this.props.tournament)) {
      reports.push(
        { key: 'start', name: 'Tournament start list', description: 'Start list for this individual tournament.' },
        { key: 'results', name: 'Tournament results', description: 'Results for this individual tournament.' },
        { key: 'meet', name: 'Meet pack', description: 'Download the complete meet pack for this tournament.' }
      );
    }
    if (isTeamTournament(this.props.tournament)) {
      reports.push({
        key: 'scores',
        name: 'Tournament score cards',
        description: 'Printable score cards for this team tournament.'
      });
    }
    return reports;
  }

  getActiveReportTitle = (): string => {
    const activeReport = this.getReports().find(report => report.key === this.state.activeReport);
    return activeReport ? activeReport.name : 'Tournament report';
  };

  renderParametersTitle = (): React.ReactNode => (
    <div className="unionTournamentReportTitle">
      <span className="unionTournamentReportTitle__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h5" />
        </svg>
      </span>
      <span>
        <strong>{this.getActiveReportTitle()}</strong>
        <small>Tournament report</small>
      </span>
    </div>
  );

  renderForm() {
    const common: any = {
      onCloseClick: this.closeParameters,
      onSubmit: this.submit,
      tournament: this.props.tournament
    };
    switch (this.state.activeReport) {
      case 'schools':
        return <TournamentSchoolsReportForm {...common} />;
      case 'members':
        return <TournamentTeamMembersReportForm {...common} />;
      case 'confirmations':
        return <TournamentParticipationConfirmationsReportForm {...common} />;
      case 'players':
        return <TournamentPlayersReportForm {...common} />;
      case 'contacts':
        return <HighLevelTournamentSchoolAdminsReportForm {...common} />;
      case 'start':
        return <TournamentStartListReportForm {...common} />;
      case 'results':
        return <TournamentResultsIndividualReportForm {...common} />;
      case 'scores':
        return <TournamentScoreCardsReportForm {...common} />;
      default:
        return null;
    }
  }

  render() {
    const { isOpen, onClose } = this.props;
    const { activeReport, isLoading, error } = this.state;
    return (
      <>
        <SimpleModal
          customClass="eEventPrintModal unionTournamentReportsModal"
          isOpen={isOpen && !activeReport}
          title="Printed forms & reports"
          onCloseClick={onClose}
          showFooter={false}
          isBodyBold={false}
        >
          <div className="eEventPrintModal__intro">
            Choose a report. You can configure its contents on the next step.
          </div>
          <section className="eEventPrintModal__group">
            <h3 className="eEventPrintModal__groupTitle">This tournament</h3>
            <div className="eEventPrintModal__list">
              {this.getReports().map(report => (
                <button
                  type="button"
                  className="eEventPrintModal__item"
                  key={report.key}
                  onClick={() => this.onReportClick(report)}
                  disabled={isLoading}
                >
                  <span className="eEventPrintModal__itemIcon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h5" />
                    </svg>
                  </span>
                  <span className="eEventPrintModal__itemCopy">
                    <span className="eEventPrintModal__itemHeading">
                      <span>{report.name}</span>
                      <span className="eEventPrintModal__format">{report.key === 'meet' ? 'ZIP' : 'Excel'}</span>
                    </span>
                    <span className="eEventPrintModal__itemDescription">{report.description}</span>
                  </span>
                  <span className="eEventPrintModal__arrow" aria-hidden="true">
                    ›
                  </span>
                </button>
              ))}
            </div>
          </section>
          {isLoading ? <div className="unionTournamentReportStatus">Preparing meet pack…</div> : null}
          {error ? <div className="unionTournamentReportStatus is-error">{error}</div> : null}
        </SimpleModal>
        <SimpleModal
          customClass="unionTournamentReportParametersModal"
          isOpen={Boolean(activeReport)}
          title={this.renderParametersTitle()}
          onCloseClick={() => !isLoading && this.closeParameters()}
          bodyClassName="unionTournamentReportParametersModal__body"
          dialogStyle={{ maxWidth: 480 }}
          showFooter={false}
          isBodyBold={false}
        >
          {this.renderForm()}
          {isLoading ? (
            <div className="unionTournamentReportLoading" role="status" aria-live="polite">
              <span className="unionTournamentReportLoading__spinner" aria-hidden="true" />
              <strong>Generating report</strong>
              <small>This may take a moment.</small>
            </div>
          ) : null}
          {error ? <div className="unionTournamentReportStatus is-error">{error}</div> : null}
        </SimpleModal>
      </>
    );
  }
}
